import { useCallback, useState } from 'react';
import { createFileRoute, Outlet, useBlocker, useChildMatches, useRouter } from '@tanstack/react-router';
import { useRealmAuthContext, useRealmAuthIsAuthenticating } from '@interxfi/api-client';
import { useActiveRealmAPIContext } from '@interxfi/api-client/realm-context';
import { FrameLoadingTrigger } from '@interxfi/design-system/components/frame/context';
import { Layout } from '../components/layout/layout';
import { SwitchTenantDialog } from '../components/layout/switch-tenant-dialog/switch-tenant-dialog';
import { NoOrganizationLayout } from '../components/no-organization-found/no-organization-layout';
import { RealmIndicatorContextProvider } from '../components/realm/realm-indicator-context';
import type { FileRoutesById } from '../routeTree.gen';
import { DEFAULT_UNAUTHENTICATED_ROUTE, MFA_SETUP_ROUTE } from '../util/routing/defaults';
import { checkAccess, type RouteGuardOptions } from '../util/routing/guard';

export const Route = createFileRoute('/_app')({
  staticData: {
    guard: {
      auth: { requireAuthenticated: true, redirectToIfNotMet: DEFAULT_UNAUTHENTICATED_ROUTE, redirectToIfMfaRequirementNotMet: MFA_SETUP_ROUTE },
    },
  } as const satisfies RouteGuardOptions,
  component: AppLayout,
});

const ALLOWED_NO_ACCESS_PAGES = ['/settings/personal', '/logout'] as const;

function getApplicableGuards(match: FileRoutesById[keyof FileRoutesById] | undefined) {
  const guards: RouteGuardOptions['guard'][] = [];

  if (!match) {
    return guards;
  }

  let node = match;
  while (node) {
    if (node.options.staticData && 'guard' in node.options.staticData) {
      guards.unshift(node.options.staticData.guard as RouteGuardOptions['guard']);
    }

    node = node.parentRoute;
  }

  return guards;
}

// TODO: this is pretty much the same as the function in __root, but adds less context, maybe there's a way to unify logic
function reduceAuthGuard(guards: RouteGuardOptions['guard'][]) {
  return guards.reduce<RouteGuardOptions['guard'] | undefined>((acc, guard) => {
    if (('auth' in guard && guard.auth) || ('access' in guard && guard.access)) {
      const newAcc = acc || {};

      if ('auth' in guard && guard.auth) {
        // Always take the furthest down auth parameters
        newAcc.auth = guard.auth;

        if (guard.auth.redirectToIfMfaRequirementNotMet) {
          newAcc.auth.redirectToIfMfaRequirementNotMet = guard.auth.redirectToIfMfaRequirementNotMet;
        }
      }

      if ('access' in guard && guard.access) {
        if (!newAcc.access) {
          newAcc.access = { allowedTenantTypes: guard.access.allowedTenantTypes };
          return newAcc;
        }

        newAcc.access.allowedTenantTypes = newAcc.access.allowedTenantTypes.filter((currentAllowedAccess) =>
          guard.access?.allowedTenantTypes.includes(currentAllowedAccess),
        );
      }

      return newAcc;
    }

    return acc;
  }, undefined);
}

function AppLayout() {
  const { activeRealmAccess } = useActiveRealmAPIContext();
  const context = useRealmAuthContext();
  const isAuthenticating = useRealmAuthIsAuthenticating();
  const { routesById } = useRouter();
  const [pendingTenantId, setPendingTenantId] = useState<string | undefined>();
  const [pendingRealmId, setPendingRealmId] = useState<string | undefined>();
  const childMatches = useChildMatches();
  const isOnAllowedNoAccessPage = childMatches.some((match) =>
    ALLOWED_NO_ACCESS_PAGES.includes(match.fullPath as (typeof ALLOWED_NO_ACCESS_PAGES)[number]),
  );

  const { status, proceed, reset } = useBlocker({
    enableBeforeUnload: false,
    shouldBlockFn({ action, next }) {
      if (action !== 'BACK') {
        return false;
      }

      const nextRoute = routesById[next.routeId];
      const guard = reduceAuthGuard(getApplicableGuards(nextRoute));

      if (!guard?.access) {
        return false;
      }

      const accessOutcome = checkAccess(guard.access, activeRealmAccess, context);

      if (accessOutcome.match || !accessOutcome.prospects.length) {
        return false;
      }

      const pendingAccess =
        accessOutcome.prospects.find((prospect) => prospect.realm?.realmId === activeRealmAccess?.realm?.realmId) || accessOutcome.prospects[0];

      // This shouldn't hit
      if (!pendingAccess.realm?.realmId || !pendingAccess.tenant?.tenantId) {
        return false;
      }

      setPendingRealmId(pendingAccess.realm.realmId);
      setPendingTenantId(pendingAccess.tenant.tenantId);

      return true;
    },
    withResolver: true,
  });

  const handleTenantSwitchOpenChange = useCallback((newOpen: boolean) => {
    if (!newOpen) {
      try {
        setPendingRealmId(undefined);
        setPendingTenantId(undefined);
      } catch {}
    }
  }, []);

  if (!isAuthenticating && !context?.accesses?.length && !isOnAllowedNoAccessPage) {
    return <NoOrganizationLayout />;
  }

  return (
    <RealmIndicatorContextProvider>
      <Layout>
        {isAuthenticating ? <FrameLoadingTrigger /> : <Outlet key={activeRealmAccess?.access?.realmAccessId} />}

        <SwitchTenantDialog
          isOpen={Boolean(status === 'blocked' && pendingRealmId && pendingTenantId)}
          onCancel={reset}
          onConfirm={proceed}
          onOpenChange={handleTenantSwitchOpenChange}
          targetRealmId={pendingRealmId!}
          targetTenantId={pendingTenantId!}
          trigger={null}
        />
      </Layout>
    </RealmIndicatorContextProvider>
  );
}
